/**
 * useGpsTracking — EPIC 1 (Tickets 1-4, 7, 8, 19)
 * Enhanced GPS tracking hook using watchPosition() for continuous position sharing.
 *
 * T1: GPS permission required before going ONLINE
 * T2: watchPosition() for continuous tracking (replaces getCurrentPosition intervals)
 * T3: Sends last known position to backend every 30s
 * T4: Clean stop on OFFLINE (clearWatch, clearInterval, reset)
 * T19: Double tracking protection (single watchId, single interval)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import config from 'config';

const API = config.API_URL;
const SEND_INTERVAL = 30000;       // Send point every 30s
const GPS_LOSS_TIMEOUT = 90000;    // GPS lost if no watchPosition callback in 90s

export function useGpsTracking() {
  const [gpsStatus, setGpsStatus] = useState('inactive'); // inactive | active | lost | error | weak
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [lastGpsTime, setLastGpsTime] = useState(null);
  const [gpsError, setGpsError] = useState(null);

  // Refs to avoid stale closures and prevent double tracking (T19)
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const lossTimerRef = useRef(null);
  const lastPositionRef = useRef(null);
  const taskIdRef = useRef(null);
  const isTrackingRef = useRef(false);

  const getHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`
  }), []);

  // ── T1: Request GPS permission (returns position or throws) ──
  const requestGpsPermission = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Géolocalisation non supportée par votre navigateur'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        }),
        (err) => {
          if (err.code === 1) {
            reject(new Error('Permission GPS refusée. Activez la géolocalisation dans les paramètres de votre navigateur.'));
          } else if (err.code === 2) {
            reject(new Error('Position GPS indisponible. Vérifiez que le GPS est activé.'));
          } else {
            reject(new Error('Délai GPS dépassé. Réessayez en extérieur.'));
          }
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  }, []);

  // ── Internal: Reset loss timer on each successful position ──
  const resetLossTimer = useCallback(() => {
    if (lossTimerRef.current) clearTimeout(lossTimerRef.current);
    lossTimerRef.current = setTimeout(() => {
      setGpsStatus('lost');
    }, GPS_LOSS_TIMEOUT);
  }, []);

  // Ref for offline queue callback (T13)
  const enqueueRef = useRef(null);

  // Set the offline queue enqueue function from outside
  const setEnqueueFn = useCallback((fn) => {
    enqueueRef.current = fn;
  }, []);

  // ── Internal: Send last known position to backend ──
  const sendPosition = useCallback(async () => {
    const pos = lastPositionRef.current;
    if (!pos) return;

    try {
      const payload = {
        lat: pos.lat,
        lng: pos.lng,
        accuracy: pos.accuracy,
      };
      // If tracking a specific task, include task_id
      if (taskIdRef.current) {
        payload.task_id = taskIdRef.current;
      }

      await axios.post(`${API}/api/driver/tracking/point`, payload, {
        headers: getHeaders(),
        timeout: 10000,
      });
    } catch (err) {
      console.warn('[GPS] Envoi position échoué:', err.message);
      // T13: Queue failed point for offline retry
      if (enqueueRef.current && pos) {
        enqueueRef.current({
          lat: pos.lat,
          lng: pos.lng,
          accuracy: pos.accuracy,
          task_id: taskIdRef.current,
        });
      }
    }
  }, [getHeaders]);

  // ── T2: Start watchPosition() for continuous tracking ──
  const startTracking = useCallback(async (taskId = null) => {
    // T19: Prevent double tracking
    if (isTrackingRef.current) {
      console.warn('[GPS] Tracking déjà actif, ignoré');
      return;
    }

    setGpsError(null);
    taskIdRef.current = taskId;

    if (!navigator.geolocation) {
      setGpsError('Géolocalisation non supportée');
      setGpsStatus('error');
      return;
    }

    try {
      // T1: Get initial position to confirm GPS works
      const initialPos = await requestGpsPermission();
      lastPositionRef.current = initialPos;
      setGpsAccuracy(Math.round(initialPos.accuracy));
      setLastGpsTime(Date.now());
      setGpsStatus(initialPos.accuracy > 100 ? 'weak' : 'active');
      isTrackingRef.current = true;

      // Send initial position to backend (start tracking)
      try {
        const startPayload = {
          lat: initialPos.lat,
          lng: initialPos.lng,
          accuracy: initialPos.accuracy,
        };
        if (taskId) startPayload.task_id = taskId;
        await axios.post(`${API}/api/driver/tracking/start`, startPayload, {
          headers: getHeaders(),
          timeout: 10000,
        });
      } catch (err) {
        console.warn('[GPS] Start tracking API failed:', err.message);
      }

      // T2: Launch watchPosition for continuous updates
      watchIdRef.current = navigator.geolocation.watchPosition(
        (pos) => {
          const newPos = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          lastPositionRef.current = newPos;
          setGpsAccuracy(Math.round(pos.coords.accuracy));
          setLastGpsTime(Date.now());
          setGpsStatus(pos.coords.accuracy > 100 ? 'weak' : 'active');
          resetLossTimer();
        },
        (err) => {
          console.warn('[GPS] watchPosition error:', err.message);
          // Don't immediately set error — allow loss timer to handle transient failures
        },
        { enableHighAccuracy: true, timeout: 30000, maximumAge: 5000 }
      );

      // T3: Send last known position every 30s
      intervalRef.current = setInterval(sendPosition, SEND_INTERVAL);

      // Start GPS loss timer
      resetLossTimer();

    } catch (err) {
      setGpsError(err.message || 'Erreur GPS');
      setGpsStatus('error');
      isTrackingRef.current = false;
    }
  }, [requestGpsPermission, sendPosition, resetLossTimer, getHeaders]);

  // ── T4: Stop tracking cleanly ──
  const stopTracking = useCallback(async () => {
    // T19: Clear watchPosition
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }

    // T19: Clear send interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Clear loss timer
    if (lossTimerRef.current) {
      clearTimeout(lossTimerRef.current);
      lossTimerRef.current = null;
    }

    // Reset state
    lastPositionRef.current = null;
    taskIdRef.current = null;
    isTrackingRef.current = false;
    setGpsStatus('inactive');
    setGpsAccuracy(null);
    setLastGpsTime(null);
    setGpsError(null);

    // Notify backend
    try {
      await axios.post(`${API}/api/driver/tracking/stop`, {}, {
        headers: getHeaders(),
        timeout: 5000,
      });
    } catch {
      // Silent fail — best effort
    }
  }, [getHeaders]);

  // ── Retry after loss or error ──
  const retryTracking = useCallback(() => {
    stopTracking().then(() => {
      startTracking(taskIdRef.current);
    });
  }, [stopTracking, startTracking]);

  // ── T15: Handle visibility change (re-check GPS when app becomes visible) ──
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isTrackingRef.current) {
        // Re-send a position immediately when app comes back to foreground
        sendPosition();
        resetLossTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [sendPosition, resetLossTimer]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (lossTimerRef.current) clearTimeout(lossTimerRef.current);
      isTrackingRef.current = false;
    };
  }, []);

  return {
    gpsStatus,             // 'inactive' | 'active' | 'weak' | 'lost' | 'error'
    gpsAccuracy,           // meters (number) or null
    lastGpsTime,           // timestamp ms or null
    gpsError,              // error message or null
    requestGpsPermission,  // T1: Request permission before ONLINE
    startTracking,         // T2: Start watchPosition + interval
    stopTracking,          // T4: Clean stop
    retryTracking,         // Retry after failure
    setEnqueueFn,          // T13: Connect offline queue
    isTracking: isTrackingRef.current || gpsStatus === 'active' || gpsStatus === 'weak',
    isLost: gpsStatus === 'lost',
    isWeak: gpsStatus === 'weak',
    lastPosition: lastPositionRef.current,
  };
}
